import PaymentContext from 'context/PaymentContext';
import { useEffectOnce } from 'hooks/useEffectOnce';
import React, { useCallback, useContext, useState, useId } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';
import { message, Tooltip } from 'antd';
import ViewListData from './ViewListData';
import './meetinghistory.css';
import FilterModal from 'components/Modal/FilterModal';
import { filter } from 'assets/images';
import DemoContext from 'context/DemoContext';
import { DATE_FORMATS, GENERIC_ERROR_MESSAGE, SORTING_STATUS, PROS_CONS_TOGGLE } from 'utils/constants';
import { TEST_IDS } from './testIds';
import { CaretDown, CaretUp } from 'react-bootstrap-icons';
import InfiniteScroll from 'react-infinite-scroll-component';

/**
 * MeetingStoreViewProsAndCons Component - Displays filtered demos based on pros/cons selection
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string|number} props.itemId - The ID of the selected pros/cons item
 * @param {string} props.type - The type of data ('pros' or 'cons')
 * @param {string} props.userType - The user type ('host' or 'guest')
 * @return {ReactNode} A React element that renders filtered demo history with filters and pagination
 */

const MeetingStoreViewProsAndCons = ({ itemId, type, userType }) => {
  const {
    storePayoutGet,
    storePayoutGetWithPagination,
    demoHistoryStoreWithPagination,
    setDemoHistoryStoreWithPagination,
    nextStorePayout,
  } = useContext(PaymentContext);

  const { fetchAllHosts, fetchAllProducts } = useContext(DemoContext);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [filteredDataLoading, setFilteredDataLoading] = useState(false);
  const [clearFiltersLoading, setClearFiltersLoading] = useState(false);
  const [filterShow, setFilterShow] = useState(false);

  const [dateFilter, setDateFilter] = useState('');
  const [originalDateFilter, setOriginalDateFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [originalStartDateFilter, setOriginalStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [originalEndDateFilter, setOriginalEndDateFilter] = useState('');
  const [hostFilter, setHostFilter] = useState('');
  const [originalHostFilter, setOriginalHostFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [originalProductFilter, setOriginalProductFilter] = useState('');
  const [sorting, setSorting] = useState({
    date: SORTING_STATUS.NONE,
    productName: SORTING_STATUS.NONE,
    result: SORTING_STATUS.NONE,
    duration: SORTING_STATUS.NONE,
    guestName: SORTING_STATUS.NONE,
    hostName: SORTING_STATUS.NONE,
  });

  // Generate unique ID for the scrollable div
  const scrollableDivId = useId().replace(/:/g, '') + '-scrollable';

  /**
   * This function is used to load more data when scrolled to the bottom of the page
   */
  const fetchMoreData = useCallback(async () => {
    if (!nextStorePayout || loading) {
      setHasMore(false);
      return;
    }
    try {
      setLoading(true);
      const nextPathname = new URL(nextStorePayout).search;
      await storePayoutGetWithPagination(nextPathname);
      if (nextStorePayout) {
        setHasMore(true);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setHasMore(false);
      messageApi.open({
        type: "error",
        content: error?.message || GENERIC_ERROR_MESSAGE,
        duration: 5,
      });
    }
  }, [nextStorePayout, loading, storePayoutGetWithPagination, messageApi]);

  /**
   * This function is used to get the current sorting state
   */
  const getCurrentSorting = () => {
    const keys = Object.keys(sorting);
    for (let index = 0; index < keys.length; index++) {
      if (
        sorting[keys[index]] === SORTING_STATUS.ASCENDING ||
        sorting[keys[index]] === SORTING_STATUS.DESCENDING
      ) {
        return [keys[index], sorting[keys[index]]];
      }
    }
    return [null, null];
  };

  /**
   * This function is the on close function which manages the states on close of the filter modal
   */
  const handleFilterClose = (setAllStates) => {
    if (setAllStates) {
      setDateFilter(originalDateFilter);
      setStartDateFilter(originalStartDateFilter);
      setEndDateFilter(originalEndDateFilter);
      setHostFilter(originalHostFilter);
      setProductFilter(originalProductFilter);
    }
    setFilterShow(false);
  };

  useEffectOnce(() => {
    // Build query parameters based on type and userType
    let queryParams = `?order=true`;
    
    if (type === PROS_CONS_TOGGLE.PROS) {
      queryParams += `&${userType}_pros=${itemId}`;
    } else {
      queryParams += `&${userType}_cons=${itemId}`;
    }

    const res = storePayoutGet(queryParams);
    res
      .then(() => setLoading(false))
      .catch((err) => {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: err?.message || GENERIC_ERROR_MESSAGE,
          duration: 5,
        });
      });

    fetchAllHosts('?')
      .then(() => {})
      .catch((err) => {
        console.log(err);
      });

    fetchAllProducts('?')
      .then(() => {})
      .catch((err) => {
        console.log(err);
      });

    return () => {
      setDemoHistoryStoreWithPagination([]);
    }
  }, [itemId, type, userType]);

  /**
   * This function is used to handle the api call with all the filters when filters are applied.
   */
  const applyFilters = () => {
    setFilteredDataLoading(true);
    let startD = '';
    let endD = '';
    if (startDateFilter) {
      startD = startDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }
    if (endDateFilter) {
      endD = endDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }

    const currentSorting = getCurrentSorting();
    let queryParams = `?order=true&startDate=${startD}&endDate=${endD}&host=${hostFilter}&product=${productFilter}`;
    
    // Add pros/cons filter
    if (type === PROS_CONS_TOGGLE.PROS) {
      queryParams += `&${userType}_pros=${itemId}`;
    } else {
      queryParams += `&${userType}_cons=${itemId}`;
    }

    if (currentSorting[0]) {
      queryParams += `&${currentSorting[0]}=${currentSorting[1]}`;
    }

    const res = storePayoutGet(queryParams);

    res
      .then((response) => {
        setFilteredDataLoading(false);
        setOriginalDateFilter(dateFilter);
        setOriginalStartDateFilter(startDateFilter);
        setOriginalEndDateFilter(endDateFilter);
        setOriginalHostFilter(hostFilter);
        setOriginalProductFilter(productFilter);
        setHasMore(response?.data?.data?.next ? true : false);
        handleFilterClose();
      })
      .catch((err) => {
        setFilteredDataLoading(false);
        messageApi.open({
          type: "error",
          content: err?.message || GENERIC_ERROR_MESSAGE,
          duration: 5,
        });
      });
  };

  /**
   * This function is used to handle the api call for clearing all the filters when filters are removed.
   */
  const clearFilters = () => {
    setClearFiltersLoading(true);

    let queryParams;
    const currentSorting = getCurrentSorting();

    // Build base query with pros/cons filter
    if (type === 'pros') {
      queryParams = `?order=true&${userType}_pros=${itemId}`;
    } else {
      queryParams = `?order=true&${userType}_cons=${itemId}`;
    }

    if (currentSorting[0]) {  
      queryParams += `&${currentSorting[0]}=${currentSorting[1]}`;
    }

    const res = storePayoutGet(queryParams);

    res
      .then((response) => {
        setClearFiltersLoading(false);
        setOriginalDateFilter('');
        setOriginalStartDateFilter('');
        setOriginalEndDateFilter('');
        setOriginalHostFilter('');
        setOriginalProductFilter('');
        setDateFilter('');
        setStartDateFilter('');
        setEndDateFilter('');
        setHostFilter('');
        setProductFilter('');
        handleFilterClose();
        setHasMore(true);
      })
      .catch((err) => {
        setClearFiltersLoading(false);
        messageApi.open({
          type: "error",
          content: err?.message || GENERIC_ERROR_MESSAGE,
          duration: 5,
        });
      });
  };

  /**
   * This function is used to get the list of history of demos
   */
  const getlist = () => {
      if (!loading && demoHistoryStoreWithPagination.length < 1) {
        return (
          <tr>
            <td>No data available!</td>
          </tr>
        );
      } else {
        return demoHistoryStoreWithPagination?.map((dataList, index) => {
          return <ViewListData key={index} dataList={dataList} />;
        });
      }
  };

  /**
   * This function is used to handle sorting on the table columns
   */
  const handleSorting = (column) => {
    if (loading) {
      return;
    }
    let startD = '';
    let endD = '';
    let sortingDirection = '';
    if (startDateFilter) {
      startD = startDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }
    if (endDateFilter) {
      endD = endDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }

    if (sorting[column] === SORTING_STATUS.ASCENDING) {
      sortingDirection = SORTING_STATUS.DESCENDING;
    } else if (sorting[column] === SORTING_STATUS.DESCENDING) {
      sortingDirection = SORTING_STATUS.NONE;
    } else {
      sortingDirection = SORTING_STATUS.ASCENDING;
    }

    setDemoHistoryStoreWithPagination([]);

    let queryParams = `?order=true&startDate=${startD}&endDate=${endD}&host=${hostFilter}&product=${productFilter}&${column}=${sortingDirection}`;
    
    // Add pros/cons filter
    if (type === PROS_CONS_TOGGLE.PROS) {
      queryParams += `&${userType}_pros=${itemId}`;
    } else {
      queryParams += `&${userType}_cons=${itemId}`;
    }

    const res = storePayoutGet(queryParams);
    setLoading(true);
    res
      .then(() => {
        const newSorting = { ...sorting };
        Object.entries(newSorting).forEach(([key, value]) => {
          if (key === column && value === SORTING_STATUS.ASCENDING) {
            newSorting[key] = SORTING_STATUS.DESCENDING;
          } else if (
            key === column &&
            sorting[column] === SORTING_STATUS.DESCENDING
          ) {
            newSorting[key] = SORTING_STATUS.NONE;
          } else if (
            key === column &&
            sorting[column] === SORTING_STATUS.NONE
          ) {
            newSorting[key] = SORTING_STATUS.ASCENDING;
          } else {
            newSorting[key] = SORTING_STATUS.NONE;
          }
        });
        setLoading(false);
        setHasMore(true);
        setSorting(newSorting);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  /**
   * This function is used to get the tooltip message
   */
  const getTooltipTitle = (columnName) => {
    if (sorting[columnName] === SORTING_STATUS.ASCENDING) {
      return 'Click here to sort descending';
    } else if (sorting[columnName] === SORTING_STATUS.DESCENDING) {
      return 'Click here to clear sorting';
    } else {
      return 'Click here to sort ascending';
    }
  };

  return (
    <>
    {contextHolder}
      <div className="filter-btn">
        <Button
          data-testid={TEST_IDS.FILTERS_BTN}
          onClick={() => setFilterShow(true)}
        >
          <img src={filter} alt="" /> Filters
        </Button>
      </div>
      <div className="table-container" id={scrollableDivId} style={{ height: '600px', overflow: 'auto' }}>
        <InfiniteScroll
          dataLength={demoHistoryStoreWithPagination.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            loading && <div className="d-flex justify-content-center">
              <Spinner animation="grow" />
              <Spinner animation="grow" />
              <Spinner animation="grow" />
            </div>
          }
          scrollableTarget={scrollableDivId}
          endMessage={
            <div className='text-center py-3 text-muted'>
              {!hasMore && "No more data to load."}
            </div>
          }
        >
      <div className="store-payout-history-wrapper table-responsive">
        <Table className="table-store-payout">
          <thead>
            <tr>
              <th className="date-head">
                <div className="arrows">
                  Date{' '}
                  <Tooltip title={getTooltipTitle('date')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('date');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="product-head">
                <div className="arrows">
                  Product{' '}
                  <Tooltip title={getTooltipTitle('productName')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('productName');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="result-head">
                <div className="arrows">
                  Result{' '}
                  <Tooltip title={getTooltipTitle('result')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('result');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="duration-head">
                <div className="arrows">
                  Duration{' '}
                  <Tooltip title={getTooltipTitle('duration')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('duration');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="host-head">
                <div className="arrows">
                  Real Customer{' '}
                  <Tooltip title={getTooltipTitle('hostName')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('hostName');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="guest-head">
                <div className="arrows">
                  Guest{' '}
                  <Tooltip title={getTooltipTitle('guestName')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('guestName');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
            </tr>
          </thead>
          <tbody id="scrollableDiv">{getlist()}</tbody>
        </Table>
      </div>
      </InfiniteScroll>
      </div>
      
      <FilterModal
        showStatus={filterShow}
        clickStatus={handleFilterClose}
        buttonMessage="Close"
        closeStatus={handleFilterClose}
        secondMessage="none"
        secondButton={false}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        setStartDateFilter={setStartDateFilter}
        setEndDateFilter={setEndDateFilter}
        setHostFilter={setHostFilter}
        setProductFilter={setProductFilter}
        startDateFilter={startDateFilter}
        endDateFilter={endDateFilter}
        hostFilter={hostFilter}
        productFilter={productFilter}
        applyFilters={applyFilters}
        originalDateFilter={originalDateFilter}
        originalStartDateFilter={originalStartDateFilter}
        originalEndDateFilter={originalEndDateFilter}
        originalHostFilter={originalHostFilter}
        originalProductFilter={originalProductFilter}
        clearFilters={clearFilters}
        loading={filteredDataLoading}
        clearFiltersLoading={clearFiltersLoading}
        parentContainer={itemId ? null : document.body}
      />
    </>
  );
};

export default MeetingStoreViewProsAndCons;
